import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { Overline } from "../components/Reveal";

export default function Auth() {
  const [mode, setMode] = useState("login");
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", referral_code: params.get("ref") || "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const next = params.get("next") || "/account";

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register(form);
      navigate(next);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full bg-transparent border border-border px-4 py-3.5 text-sm outline-none focus:border-wine transition-colors placeholder:text-muted-foreground/60";

  return (
    <div className="min-h-[80vh] grid lg:grid-cols-2" data-testid="auth-page">
      <div className="hidden lg:block relative overflow-hidden">
        <img src="https://images.unsplash.com/photo-1601597565151-70c4020dc0e1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwzfHxlZGl0b3JpYWwlMjBmYXNoaW9uJTIwcG9ydHJhaXR8ZW58MHx8fHwxNzg2Njg4NTI2fDA&ixlib=rb-4.1.0&q=85"
          alt="Editorial" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-ink/30" />
        <p className="absolute bottom-10 left-10 font-serif text-4xl text-ivory max-w-sm leading-tight">Premium Fashion. <span className="italic text-gold">Give It Another Life.</span></p>
      </div>
      <div className="flex items-center justify-center px-5 py-16">
        <div className="w-full max-w-md">
          <Overline>{mode === "login" ? "Welcome Back" : "Join Jigel"}</Overline>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mt-3">{mode === "login" ? "Sign in." : "Create account."}</h1>
          <form onSubmit={submit} className="mt-10 space-y-5" data-testid="auth-form">
            {mode === "register" && (
              <>
                <input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Full name" required data-testid="auth-name" className={inputCls} />
                <input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Phone number" required data-testid="auth-phone" className={inputCls} />
                <input value={form.referral_code} onChange={(e) => set("referral_code", e.target.value.toUpperCase())} placeholder="Referral code (optional)" data-testid="auth-referral" className={`${inputCls} font-mono tracking-widest`} />
              </>
            )}
            <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="Email address" required data-testid="auth-email" className={inputCls} />
            <input type="password" value={form.password} onChange={(e) => set("password", e.target.value)} placeholder="Password (min 8 characters)" required minLength={8} data-testid="auth-password" className={inputCls} />
            {error && <p className="text-sm text-destructive" data-testid="auth-error">{error}</p>}
            <button type="submit" disabled={busy} data-testid="auth-submit-btn"
              className="w-full bg-ink text-ivory py-4 text-[11px] tracking-[0.25em] uppercase hover:bg-wine transition-colors disabled:opacity-50">
              {busy ? "One moment…" : mode === "login" ? "Sign In" : "Create Account"}
            </button>
          </form>
          <button onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }} data-testid="auth-toggle-mode"
            className="mt-6 text-[11px] tracking-[0.2em] uppercase text-muted-foreground hover:text-wine transition-colors">
            {mode === "login" ? "New to Jigel? Create an account" : "Already have an account? Sign in"}
          </button>
          <p className="mt-10 text-[11px] text-muted-foreground leading-relaxed">
            By continuing you agree to shop thoughtfully. <Link to="/sell" className="text-wine underline underline-offset-4">Sell your clothes</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
